import {Component, Input, OnInit} from "@angular/core";
import {BoosterMode, BoosterService} from "../../../services/booster.service";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {NotifierComponent} from "../../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../../shared/components/notifier/notification-type";
import {TextUtils} from "../../../../../../shared/utils/text-utils";
import modes from "../../../settings/modes.json";
import {TypeUtils} from "../../../../../../shared/utils/type-utils";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../../store";
import {DbStoreState} from "../../../../../../store";
import {GoogleAnalyticsService} from "../../../../../../shared/services/google-analytics/google-analytics.service";
import {PerformanceService} from "../../../../performance/performance.service";
import {BoosterPage} from "../../../mappers/booster-page";
import {UserSubscription} from "../../../../../../user/user-subscription";
import {ActivatedRoute, Router} from "@angular/router";
import {User} from "../../../../../../user/user";
import {combineLatest} from "rxjs";
import {Subject} from "rxjs";
import {DbResponse} from "../../../../../../shared/interceptors/db-response";
import {UserService} from "../../../../../../user/user.service";
import {DbModalService} from "../../../../../../modules/modal/modal";
import {Config} from "../../../../../../config/config";
import {SignUpFor, TypeFormAnswers} from "../../../../../../add-website/typeFormAnswers";
import {DialogService} from "../../../../../../../ng2-bootstrap-modal/dialog.service";
import {
  NotificationDialogComponent
} from "../../../../../../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {
  AccountUpgradedDialogComponent
} from "../../../../../../shared/components/dialogs/account-upgraded-dialog/account-upgraded-dialog.component";
import {
  OneMonthFreeComponent
} from "../../../../../../shared/components/dialogs/one-month-free/one-month-free.component";
import {ApplyProUpgradeDialogComponent} from "../dialogs/apply-pro-upgrade-dialog/apply-pro-upgrade-dialog.component";
import {Utils} from "../../../../../../shared/utils/utils";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {BuilderSectionsInteractionService} from "../../../../../../builder-v2/builder-sections-interaction.service";

interface Mode {
  id: BoosterMode;
  key: number;
  title: string;
  desc: string;
}

interface IPageSpeedData {
  title: string;
  withoutCDN: string | number;
  withCDN: string;
}


@Component({
    selector: 'db-optimization-levels',
    templateUrl: './optimization-levels.component.html',
    styleUrls: ['./optimization-levels.component.scss'],
    standalone: false
})
export class OptimizationLevelsComponent implements OnInit {
  public settingsOptions: Mode[] = modes;
  public optimizedPages: BoosterPage[] | null = [];
  public user: User;
  public signUpForAnswers: SignUpFor;
  public typeFormAnswers = TypeFormAnswers;
  public userLocalStorage: any;
  public loadingApply: boolean;
  public testMode: 'on' | 'off' | null = null;
  public testModeLoading: boolean = true;
  public boosterMode: BoosterMode;
  public initialBoosterMode: BoosterMode;
  public boosterModeLoading: boolean = true;
  public testModeEnabled: boolean;
  public cdnStatus: string;
  public showVerifyEmail: boolean = true;
  public Config = Config;
  public cdnIsLive: boolean;
  public demoUser: boolean = false;
  public companyName: string = '10Web';
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  @Input() boosterPluginIsActive: boolean;
  @Input() adminActivateBoosterUrl: string;
  @Input() websiteId: number;
  @Input() websiteHosted?: boolean;
  @Input() websiteCDNEnabled?: boolean;
  @Input() maxOptimizedPagesLength: number;
  @Input() userSubscription: UserSubscription;
  @Input() notOptimizedPagesCount: number;
  @Input() websiteUrl: string;
  @Input() cdnIsActive: boolean;
  @Input() boosterPluginIsOld: boolean;
  @Input() websiteIsStaging: boolean;

  constructor(
    private _store: Store<DbStoreState>,
    private _router: Router,
    private _route: ActivatedRoute,
    private _userService: UserService,
    private _performanceService: PerformanceService,
    private _boosterService: BoosterService,
    private _modalService: DbModalService,
    private _dialogService: DialogService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _builderSectionsInteractionService: BuilderSectionsInteractionService
  ) {
  }

  ngOnInit() {
    this._getUser();
    this._getBoosterData();
    this._getCloudflareData();
    this._getCompanyInfo();
  }

  private _getCompanyInfo(): void {
    this._builderSectionsInteractionService.companyInfo
      .pipe(
        filter(companyInfo => !!companyInfo),
        takeUntil(this._destroyStream$),
        first()
      )
      .subscribe(companyInfo => {
        this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
      });
  }

  private _getUser(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserLocalStorage)
        )),
        takeUntil(this._destroyStream$),
        first()
      )
      .subscribe(([user, userLocalStorage]) => {
        this.user = user;
        this.signUpForAnswers = user && user.signUpFor;
        this.userLocalStorage = userLocalStorage;
        this.demoUser = Utils.isDemoUser(this.userSubscription, this.user);
        this._fromLocalStorage();
      });
  }

  private _fromLocalStorage(): void {
    const currentDate = new Date().getTime();
    const oneDay: number = 24 * 60 * 60 * 1000;
    if (localStorage.getItem("verifyEmailErrorShown" + this.user.id) === null) {
      localStorage.setItem("verifyEmailErrorShown" + this.user.id, currentDate.toString());
    } else {
      const verifyEmailShownDate = localStorage.getItem("verifyEmailErrorShown" + this.user.id);
      this.showVerifyEmail = new Date().getTime() - new Date(parseInt(verifyEmailShownDate, 10)).getTime() < oneDay;
    }
  }

  public sendDataToLocalStorage(value): void {
    this.userLocalStorage[value] = true;
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }


  private _getCloudflareData(): void {
    this._store.select(fromStore.getCloudflareData(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$),
      )
      .subscribe(data => {
        this.cdnStatus = data && data.cfStatus ? data.cfStatus : '';
      });
  }

  public closeVerifyEmailMessage(): void {
    this.userLocalStorage['verifyEmailErrorClosed'] = true;
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }

  public disableTestMode(): void {
    this.testModeLoading = true;
    this.sendEvent('Disable Test mode, general', this.boosterMode, this.websiteId);
    this._boosterService.setTestMode(this.websiteId, 'off')
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.websiteId}));
          setTimeout(() => {
            NotifierComponent.notify(new Notification("Your test mode is turned off.", NotificationType.SUCCESS));
          }, 3000);
        } else {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrongDot'), NotificationType.DANGER));
        }
      });
  }

  public selectMode(mode: BoosterMode): void {
    if (this.demoUser) {
      const analyticsData = {
        eventCategory: 'Free Upgrade Offer',
        eventAction: 'Locked button click',
        eventLabel: 'Booster Mode: ' + mode
      };

      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
      if (this.demoUser) {
        const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
        const options = {
          overflowHidden: false,
          backdropClass: 'blurred',
        };
        if (!firstTime) {
          options['width'] = 1240;
        } else {
          options['width'] = 460;
          options['height'] = 600;
        }
        const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
        modal.componentInstance.showFirstTime = firstTime;
/*
        modal.componentInstance.showAsEcommerce = true;
*/
      } else {
        const modal = this._modalService.open(OneMonthFreeComponent, {height: 600, width: 940});
        modal.componentInstance.mode = 'feature';
      }
    } else {
      this.boosterMode = mode;
      this.apply(mode);
    }
  }

  public apply(mode): void {
    this.sendEvent('Booster mode selection', mode, this.websiteId);
    this._applyBoosterMode(this.boosterMode);
  }

  private _getBoosterData(): void {
    this._getPagesWithoutFrontEnd();
    this._getModeLevel();
    this._getTestMode();
  }

  private _getModeLevel(): void {
    this._store.select(fromStore.getWebsiteBoosterModeLevel(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(modeLevel => {
        if (!TypeUtils.isNull(modeLevel)) {
          this.boosterMode = modeLevel;
          this.initialBoosterMode = modeLevel;
          this.boosterModeLoading = false;
        }
      });
  }

  private _getTestMode(): void {
    this._store.select(fromStore.getWebsiteBoosterTestMode(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(testMode => {
        if (!TypeUtils.isNull(testMode)) {
          this.testMode = testMode;
          this.testModeEnabled = !TypeUtils.isNull(this.testMode) && this.testMode === 'on';
          this.testModeLoading = false;
        }
      });
  }

  private _getPagesWithoutFrontEnd(): void {
    this._store.select(fromStore.getWebsiteBoosterPagesWithoutFrontPage(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(pages => {
        if (!TypeUtils.isNull(pages)) {
          this.optimizedPages = pages;
        }
      });
  }

  public getWpImageOptimizerPage(): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'IO: Activate Booster button click',
      eventLabel: 'IO: Booster main page'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    window.open(this.adminActivateBoosterUrl, "_blank", "noopener");
  }


  public openChat() {
    window['Smooch']?.open();
  }

  private _applyBoosterMode(mode: BoosterMode): void {
    this.loadingApply = true;
    this._boosterService.applyBoosterMode(this.websiteId, 'all', mode)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success) {
          this._performanceService.getGooglePageSpeedScore(this.websiteId);
          this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.websiteId}));
          this._store.dispatch(new fromStore.LoadBoosterAdvancedSettings(this.websiteId));
          NotifierComponent.notify(new Notification(response.message, NotificationType.SUCCESS));
        } else {
          this.boosterMode = this.initialBoosterMode;
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrongDot'), NotificationType.DANGER));
        }
        this.loadingApply = false;
      });
  }

  public showUpgradeDialog(): void {
    this.sendEvent('Apply Pro button click', 'Cloudflare Demo');
    this._modalService.open(ApplyProUpgradeDialogComponent, {height: 500, width: 500});
  }

  private _upgradedFromProLevel(): void {
    this._dialogService.addDialog(AccountUpgradedDialogComponent, {
      currentSubscription: this.userSubscription,
      upgradedFromBooster: true,
      fromPaidDialog: true
    });
  }

  public showActivateCDN(): void {
    this._dialogService.addDialog(NotificationDialogComponent, {
      header: 'Apply Pro optimization level',
      message: 'Enable and activate CDN on your website to apply<br/> Pro optimization level.',
      buttonText: 'Enable',
      buttonColor: 'green',
      action: 'enable',
      hasCloseButton: true,
    }).subscribe((confirmed: string): void => {
      if (confirmed === 'enable') {
        this.sendEvent('Enable CDN button click', 'Cloudflare Demo');
        this._router.navigate(['../cloudflare'], {relativeTo: this._route});
      }
    });
  }

  public sendEvent(action: string, mode, websiteId?: number): void {
    let analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: action,
      eventLabel: 'Booster Mode: ' + mode
    };

    if (websiteId) {
      analyticsData = Object.assign({}, analyticsData, {
        websiteId: websiteId,
      });
    }

    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

}
